import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Flower2, ArrowLeft, Award, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { modulesAPI, quizAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const QuizView = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { updateUser, user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const fetchQuiz = async () => {
      try {
        const response = await modulesAPI.getQuiz(moduleId);
        setQuiz(response.data.quiz);
        setSelectedAnswers(new Array(response.data.quiz.questions.length).fill(-1));
      } catch (error) {
        console.error('Error fetching quiz:', error);
        toast({
          title: 'Erreur',
          description: 'Impossible de charger le quiz',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchQuiz();
  }, [moduleId]);

  const handleSelectAnswer = (optionIndex) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = optionIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    if (selectedAnswers.includes(-1)) {
      toast({
        title: 'Quiz incomplet',
        description: 'Veuillez répondre à toutes les questions',
        variant: 'destructive'
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await quizAPI.submit(quiz.id, selectedAnswers);
      // Map backend fields to frontend expected fields and include quiz questions for review
      setResults({
        score: response.data.score,
        passed: response.data.passed,
        correctCount: response.data.correct,
        totalQuestions: response.data.total,
        pointsEarned: response.data.pointsEarned || 0,
        questions: quiz.questions,
      });
      
      // Update user points if passed
      if (response.data.passed && user) {
        updateUser({
          ...user,
          points: user.points + response.data.pointsEarned
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la soumission du quiz',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  if (!quiz) {
    return null;
  }

  // Show results
  if (results) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
        <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
          <div className="flex items-center gap-3">
            <Flower2 className="w-8 h-8 text-pink-300" />
            <h1 className="text-xl font-bold text-white">Résultats</h1>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Score Card */}
          <Card className={`p-8 backdrop-blur-md border-white/20 text-center ${
            results.passed 
              ? 'bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-400/30'
              : 'bg-gradient-to-br from-red-500/20 to-pink-500/20 border-red-400/30'
          }`}>
            {results.passed ? (
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-4" />
            ) : (
              <XCircle className="w-20 h-20 text-red-400 mx-auto mb-4" />
            )}
            
            <h2 className="text-3xl font-bold text-white mb-2">
              {results.passed ? 'Félicitations! 🎉' : 'Pas tout à fait...'}
            </h2>
            
            <div className="text-5xl font-bold text-white my-6">
              {results.score}%
            </div>
            
            <p className="text-pink-100 text-lg mb-4">
              {results.correctCount} / {results.totalQuestions} réponses correctes
            </p>
            
            {results.passed && (
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
                <Award className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="text-white font-semibold">
                  +{results.pointsEarned} points gagnés!
                </p>
              </div>
            )}
            
            {!results.passed && (
              <p className="text-pink-200 text-sm">
                Score minimum requis: {quiz.passingScore}%
              </p>
            )}
          </Card>

          {/* Questions Review */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-white">Révision des réponses</h3>
            {results.questions.map((question, qIndex) => (
              <Card key={question.id || `q-${qIndex}`} className="p-6 bg-white/10 backdrop-blur-md border-white/20">
                <h4 className="text-white font-semibold mb-4">
                  Question {qIndex + 1}: {question.question}
                </h4>
                
                <div className="space-y-2 mb-4">
                  {question.options.map((option, oIndex) => {
                    const isSelected = selectedAnswers[qIndex] === oIndex;
                    const isCorrect = option.isCorrect;
                    
                    let className = 'p-3 rounded-lg border-2 ';
                    if (isCorrect) {
                      className += 'border-green-400 bg-green-500/20 text-green-100';
                    } else if (isSelected) {
                      className += 'border-red-400 bg-red-500/20 text-red-100';
                    } else {
                      className += 'border-white/20 bg-white/5 text-pink-200';
                    }
                    
                    return (
                      <div key={option.id || `${question.id || qIndex}-opt-${oIndex}`} className={className}>
                        <div className="flex items-center gap-2">
                          {isCorrect && <CheckCircle className="w-5 h-5 text-green-400" />}
                          {!isCorrect && isSelected && <XCircle className="w-5 h-5 text-red-400" />}
                          <span>{option.text}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="bg-blue-500/20 border border-blue-400/30 rounded-lg p-3">
                  <p className="text-blue-100 text-sm">
                    <strong>Explication:</strong> {question.explanation}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate(`/module/${moduleId}`)}
              className="flex-1 bg-white/10 hover:bg-white/20 text-white py-6 rounded-xl"
            >
              Retour au module
            </Button>
            {!results.passed && (
              <Button 
                onClick={() => {
                  setResults(null);
                  setCurrentQuestion(0);
                  setSelectedAnswers(new Array(quiz.questions.length).fill(-1));
                }}
                className="flex-1 bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white py-6 rounded-xl"
              >
                Réessayer
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz questions
  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/module/${moduleId}`)} className="text-pink-300">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <Flower2 className="w-8 h-8 text-pink-300" />
          <h1 className="text-xl font-bold text-white">Quiz</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Progress */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-semibold">
              Question {currentQuestion + 1} / {quiz.questions.length}
            </span>
            <span className="text-pink-300">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-purple-900/50 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-pink-400 to-purple-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </Card>

        {/* Question */}
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20 mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">{question.question}</h2>
          
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={option.id || `opt-${currentQuestion}-${index}`}
                onClick={() => handleSelectAnswer(index)}
                className={`w-full p-4 rounded-xl text-left transition-all ${
                  selectedAnswers[currentQuestion] === index
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white border-2 border-pink-300'
                    : 'bg-white/5 hover:bg-white/10 text-pink-100 border-2 border-white/20'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedAnswers[currentQuestion] === index
                      ? 'border-white bg-white'
                      : 'border-pink-300'
                  }`}>
                    {selectedAnswers[currentQuestion] === index && (
                      <div className="w-3 h-3 rounded-full bg-pink-500" />
                    )}
                  </div>
                  <span className="font-medium">{option.text}</span>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            className="flex-1 bg-white/10 hover:bg-white/20 text-white py-6 rounded-xl disabled:opacity-30"
          >
            Précédent
          </Button>
          
          {currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={selectedAnswers[currentQuestion] === -1}
              className="flex-1 bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white py-6 rounded-xl disabled:opacity-50"
            >
              Suivant
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedAnswers[currentQuestion] === -1}
              className="flex-1 bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white py-6 rounded-xl disabled:opacity-50"
            >
              {submitting ? 'Envoi...' : 'Terminer'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizView;
